/**
 * PaidDownloadConfirmModal — "Confirm model download": one line per quoted
 * model version with its price (``Free`` / ``Already paid`` / ``$x.xx``), the
 * total and the workspace balance. Opened by ``useDownloadQuoteGate`` when a
 * download would charge credits; ``Download`` runs the approved request.
 */
import React from 'react';
import { Modal, SparkModalPortal, Text } from '@spark';

import { formatMoney } from '../utils/billingFormat';
import { quoteItemKey } from '../hooks/useDownloadQuoteGate';
import '../billing.css';

export const PAID_DOWNLOAD_MODAL_WIDTH = 520;
export const PAID_DOWNLOAD_TITLE = 'Confirm model download';
export const PAID_DOWNLOAD_CTA = 'Download';
export const PAID_DOWNLOAD_EXEMPT_NOTE =
    'Billing enforcement is disabled for your account; this cost is tracked but not charged.';

const CURRENCY = 'usd';

export function quotedPriceLabel(item) {
    if (item?.pricing === 'free') return 'Free';
    if (item?.pricing === 'already_recorded') return 'Already paid';
    return formatMoney(item?.cost_cents ?? 0, CURRENCY);
}

const fallbackLabel = (item) => `${item.llm_usecase_id} v${item.version}`;

export default function PaidDownloadConfirmModal({ open = false, quote, items = [], onConfirm, onCancel }) {
    const labels = new Map(items.map((item) => [quoteItemKey(item), item.label]));
    const lines = (quote?.items || []).map((item) => ({
        key: quoteItemKey(item),
        label: labels.get(quoteItemKey(item)) || fallbackLabel(item),
        price: quotedPriceLabel(item),
    }));
    const cancel = () => onCancel?.();

    return (
        <SparkModalPortal open={open} onClose={cancel} width={PAID_DOWNLOAD_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                className="billing-dialog paid-download-modal"
                title={PAID_DOWNLOAD_TITLE}
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel={PAID_DOWNLOAD_CTA}
                tertiaryLabel="Cancel"
                onPrimaryClick={() => onConfirm?.()}
                onTertiaryClick={cancel}
                onCloseClick={cancel}
            >
                <div className="billing-dialog__body" data-testid="paid-download-modal">
                    <ul className="download-quote__lines" aria-label="Models to download">
                        {lines.map((line) => (
                            <li key={line.key} className="download-quote__line">
                                <Text as="span" variant="base-regular" color="primary" className="download-quote__label">
                                    {line.label}
                                </Text>
                                <Text as="span" variant="base-regular" color="secondary" className="download-quote__price">
                                    {line.price}
                                </Text>
                            </li>
                        ))}
                    </ul>
                    <div className="download-quote__summary">
                        <div className="download-quote__line download-quote__line--total">
                            <Text as="span" variant="base-semibold" color="primary">Total</Text>
                            <Text as="span" variant="base-semibold" color="primary" className="download-quote__price">
                                {formatMoney(quote?.total_cents ?? 0, CURRENCY)}
                            </Text>
                        </div>
                        <div className="download-quote__line">
                            <Text as="span" variant="base-regular" color="secondary">Balance</Text>
                            <Text as="span" variant="base-regular" color="secondary" className="download-quote__price">
                                {formatMoney(quote?.balance_cents ?? 0, CURRENCY)}
                            </Text>
                        </div>
                    </div>
                    {quote?.enforcement_exempt ? (
                        <Text as="p" variant="base-regular" color="secondary" className="billing-dialog__copy">
                            {PAID_DOWNLOAD_EXEMPT_NOTE}
                        </Text>
                    ) : null}
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
